import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    Inbox,
    Search,
    ClipboardCheck,
    UserRound,
    Truck,
    Landmark,
    BellRing,
    FileBarChart,
    History,
    Plug,
    Settings,
    LogOut,
    Shield,
    Users,
    Building2,
    ShieldCheck,
    Server,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import CompanySelector from "./CompanySelector";

const NAV_MAIN = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/pec/inbox", label: "PEC in arrivo", icon: Inbox },
    { to: "/pec/analyses", label: "Analisi", icon: Search },
    { to: "/reviews", label: "Coda revisioni", icon: ClipboardCheck },
    { to: "/senders", label: "Mittenti", icon: UserRound },
    { to: "/suppliers", label: "Fornitori", icon: Truck },
    { to: "/ibans", label: "IBAN autorizzati", icon: Landmark },
    { to: "/rules", label: "Regole e alert", icon: BellRing },
    { to: "/reports", label: "Report", icon: FileBarChart },
    { to: "/audit", label: "Audit log", icon: History },
    { to: "/integrations", label: "Integrazioni", icon: Plug },
    { to: "/settings", label: "Impostazioni", icon: Settings },
];

const NAV_ADMIN = [
    { to: "/admin/companies", label: "Aziende", icon: Building2, superOnly: true },
    { to: "/admin/users", label: "Utenti", icon: Users, adminOrSuper: true },
    { to: "/admin/pec-providers", label: "Gestori PEC", icon: Server, superOnly: true },
    { to: "/admin/pki", label: "Trust Store PKI", icon: ShieldCheck, superOnly: true },
];

export function Sidebar({ onNavigate }) {
    const { user, isSuperAdmin, canAdmin, logout, companyId } = useAuth();
    const [inboxBadge, setInboxBadge] = React.useState(0);

    React.useEffect(() => {
        // Silently fetch the inbox in-pipeline counter every 60s
        let alive = true;
        let timer;
        const fetchCounter = async () => {
            try {
                const api = (await import("../lib/api")).default;
                // Skip when super_admin is in "all companies" mode — endpoint would 400
                if (!companyId || companyId === "__all__") { setInboxBadge(0); return; }
                const { data } = await api.get("/pec/inbox/counter");
                if (alive) setInboxBadge(data?.in_pipeline || 0);
            } catch { /* ignore — sidebar is decorative */ }
        };
        fetchCounter();
        timer = setInterval(fetchCounter, 60_000);
        return () => { alive = false; clearInterval(timer); };
    }, [companyId]);

    const linkBase =
        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors group";
    const inactive = "text-[#94A3B8] hover:text-white hover:bg-[#142340]";
    const active = "bg-[#142340] text-white border-l-2 border-[#2563EB] pl-[10px]";

    const filteredAdmin = NAV_ADMIN.filter((item) => {
        if (item.superOnly) return isSuperAdmin();
        if (item.adminOrSuper) return canAdmin();
        return false;
    });

    return (
        <aside
            data-testid="sidebar"
            className="w-[260px] shrink-0 bg-[#0B1633] h-screen sticky top-0 flex flex-col text-[#94A3B8]"
        >
            {/* Brand */}
            <Link
                to="/"
                onClick={onNavigate}
                data-testid="sidebar-brand"
                className="flex items-center gap-3 px-6 py-5 border-b border-white/5"
            >
                <div className="w-9 h-9 rounded-lg bg-[#2563EB] flex items-center justify-center shadow-lg shadow-[#2563EB]/30">
                    <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.5} />
                </div>
                <div>
                    <div className="text-white font-bold tracking-tight">IH PEC Guard</div>
                    <div className="text-[10px] text-[#64748B] uppercase tracking-wider">Cybersecurity PEC</div>
                </div>
            </Link>

            {/* Company selector */}
            <div className="px-4 py-4 border-b border-white/5">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B] mb-2 px-1">
                    Azienda attiva
                </div>
                <CompanySelector variant="sidebar" />
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto sidebar-scroll px-3 py-4 space-y-0.5">
                {NAV_MAIN.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        onClick={onNavigate}
                        data-testid={`nav-${item.to === "/" ? "root" : item.to.replace(/^\//, "").replace(/\//g, "-")}`}
                        className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}
                    >
                        <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                        <span className="truncate">{item.label}</span>
                        {item.to === "/pec/inbox" && inboxBadge > 0 && (
                            <span
                                data-testid="sidebar-inbox-badge"
                                className="ml-auto text-[10px] font-mono font-bold bg-cyan-400/20 text-cyan-300 border border-cyan-400/30 rounded-full px-1.5 py-0.5 animate-pulse"
                                title={`${inboxBadge} PEC in coda analisi`}
                            >
                                {inboxBadge}
                            </span>
                        )}
                    </NavLink>
                ))}

                {filteredAdmin.length > 0 && (
                    <>
                        <div className="mt-6 mb-2 px-3 flex items-center gap-2">
                            <Shield className="w-3 h-3 text-[#64748B]" />
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">
                                Amministrazione
                            </span>
                        </div>
                        {filteredAdmin.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                onClick={onNavigate}
                                data-testid={`nav-${item.to === "/" ? "root" : item.to.replace(/^\//, "").replace(/\//g, "-")}`}
                                className={({ isActive }) => `${linkBase} ${isActive ? active : inactive}`}
                            >
                                <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                                <span className="truncate">{item.label}</span>
                            </NavLink>
                        ))}
                    </>
                )}
            </nav>

            {/* User footer */}
            <div className="border-t border-white/5 px-3 py-4">
                <div className="px-2 py-2">
                    <div className="text-sm font-medium text-white truncate" data-testid="sidebar-user-name">
                        {user?.full_name || "—"}
                    </div>
                    <div className="text-xs text-[#64748B] truncate" data-testid="sidebar-user-email">
                        {user?.email}
                    </div>
                </div>
                <button
                    data-testid="logout-btn"
                    onClick={logout}
                    className="w-full mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#94A3B8] hover:text-white hover:bg-[#142340] transition-colors"
                >
                    <LogOut className="w-4 h-4" />
                    <span>Esci</span>
                </button>
            </div>
        </aside>
    );
}

export default Sidebar;
